import { Injectable } from '@angular/core';
import {filter, tap} from 'rxjs';
import {OAuthEvent, OAuthService, OAuthStorage} from 'angular-oauth2-oidc';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {authCodeFlowConfig} from '../config/auth-code-flow.config';
import {LoadingService} from './loading.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // ⚡ Flag pour éviter les appels concurrents
  private isInitializing = false;
  private isLoggingIn = false;

  constructor(
    private oauthService: OAuthService,
    private loading: LoadingService
  ) {
    // Configurer le service OAuth
    this.oauthService.configure(authCodeFlowConfig);
// Écoute des events OIDC
    this.oauthService.events.subscribe((event: OAuthEvent) => {
      switch (event.type) {
        // Début de l'échange du code → token
        case 'discovery_document_loaded':
          if (this.hasAuthCode()) loading.show();
          break;

        // Token reçu → chargement terminé
        case 'token_received':
        case 'token_error':
        case 'silent_refresh_error':
          loading.hide();
          break;
      }
    });
    // Écouter les événements OAuth pour le débogage
    this.oauthService.events
      .pipe(filter((e: OAuthEvent) => e.type === 'token_received'))
      .subscribe(() => {
        console.log('✅ [AuthService] Token reçu avec succès');
      });

    this.oauthService.events
      .pipe(filter((e: OAuthEvent) => e.type === 'discovery_document_loaded'))
      .subscribe(() => {
        console.log('📄 [AuthService] Discovery document chargé');
      });

    this.oauthService.events.subscribe(event => {
      console.log('🔐 [AuthService] OAuth Event:', event.type, event);
    });
  }

  // Détecte si on revient d'un redirect OIDC
  hasAuthCode(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has('code');
  }

  /**
   * Initialise l'authentification au démarrage de l'application
   * Cette méthode doit être appelée UNE SEULE FOIS via APP_INITIALIZER
   */
  async initAuth(): Promise<boolean> {
    // ⚡ Protection contre les appels concurrents
    if (this.isInitializing) {
      console.warn('⚠️ [AuthService] initAuth() déjà en cours, ignorer cet appel');
      return this.isAuthenticated();
    }

    this.isInitializing = true;

    try {
      console.log('🚀 [AuthService] Initialisation de l\'authentification...');
      console.log('🔧 [AuthService] Config:', {
        issuer: this.oauthService.issuer,
        redirectUri: this.oauthService.redirectUri,
        clientId: this.oauthService.clientId,
        responseType: this.oauthService.responseType,
        currentUrl: window.location.href
      });

      // Afficher les paramètres de l'URL
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has('code');
      const hasState = urlParams.has('state');
      const hasError = urlParams.has('error');

      console.log('📋 [AuthService] Paramètres URL:', {
        hasCode,
        hasState,
        hasError,
        code: hasCode ? urlParams.get('code')?.substring(0, 20) + '...' : 'N/A',
        state: hasCode ? urlParams.get('state')?.substring(0, 20) + '...' : 'N/A',
        error: hasError ? urlParams.get('error') : 'N/A'
      });

      // Afficher les clés dans localStorage
      const allKeys = Object.keys(localStorage);
      const oauthKeys = allKeys.filter(key =>
        key.includes('nonce') ||
        key.includes('pkce') ||
        key.includes('state') ||
        key.includes('code_verifier') ||
        key.includes('PKCE')
      );

      console.log('💾 [AuthService] TOUTES les clés localStorage:', allKeys);
      console.log('🔑 [AuthService] Clés OAuth/PKCE trouvées:', oauthKeys);

      // Avertissement si on a un code mais pas de PKCE
      if (hasCode && oauthKeys.length === 0) {
        console.error('🚨 [AuthService] PROBLÈME CRITIQUE : Code présent mais AUCUNE clé PKCE dans localStorage !');
        console.error('💡 [AuthService] Cela signifie que le flux OAuth n\'a pas été initialisé correctement.');
        console.error('💡 [AuthService] Le code ne peut pas être échangé sans le code_verifier PKCE.');
      }

      console.log('📡 [AuthService] Chargement du discovery document et traitement du callback...');

      const startTime = Date.now();

      // ⚡ CLEF: loadDiscoveryDocumentAndTryLogin() fait 2 choses:
      // 1. Charge le discovery document
      // 2. Traite le callback OAuth (échange code contre token) si présent dans l'URL
      //    ⏱️ Cette étape peut prendre plusieurs secondes (appel HTTP à /oauth2/token)
      const result = await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      const duration = Date.now() - startTime;
      console.log(`📡 [AuthService] loadDiscoveryDocumentAndTryLogin terminé en ${duration}ms, résultat:`, result);

      // Vérifier si on a des tokens valides
      console.log('🔍 [AuthService] Vérification du token...');
      const hasValidAccessToken = this.oauthService.hasValidAccessToken();
      const hasValidIdToken = this.oauthService.hasValidIdToken();

      console.log('🎫 [AuthService] État des tokens:', {
        hasValidAccessToken,
        hasValidIdToken,
        accessToken: this.oauthService.getAccessToken()?.substring(0, 20) + '...',
        idToken: this.oauthService.getIdToken()?.substring(0, 20) + '...'
      });

      if (hasValidAccessToken && hasValidIdToken) {
        console.log('✅ [AuthService] Utilisateur authentifié avec succès');

        // Récupérer le profil utilisateur
        const claims = this.oauthService.getIdentityClaims();
        console.log('👤 [AuthService] Claims utilisateur:', claims);

        return true;
      } else {
        console.warn('⚠️ [AuthService] Pas de token valide après tryLogin');
        return false;
      }
    } finally {
      this.isInitializing = false;
      console.log('🔓 [AuthService] isInitializing = false');
    }
  }

  /**
   * Échange le code OAuth contre un token (appelé par CallbackComponent).
   * Charge le discovery document puis appelle tryLogin().
   */
  async handleCallback(): Promise<boolean> {
    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      return this.isAuthenticated();
    } catch (error) {
      console.error('[AuthService] Erreur lors du callback OAuth:', error);
      return false;
    }
  }

  /**
   * Démarre le flux de connexion OAuth2 avec PKCE
   * Cette méthode est appelée par le guard quand l'utilisateur n'est pas authentifié
   */
  async login(): Promise<void> {
    // ⚡ Protection contre les appels concurrents
    if (this.isLoggingIn) {
      console.warn('⚠️ [AuthService] login() déjà en cours, ignorer cet appel');
      return;
    }

    if (this.isInitializing) {
      console.warn('⚠️ [AuthService] initAuth() en cours, attendre avant d\'appeler login()');
      return;
    }

    this.isLoggingIn = true;

    try {
      console.log('🔐 [AuthService] Démarrage du flux de connexion...');

      // Afficher l'état du localStorage AVANT initCodeFlow
      const allKeysBefore = Object.keys(localStorage);
      const oauthKeysBefore = allKeysBefore.filter(key =>
        key.includes('nonce') ||
        key.includes('pkce') ||
        key.includes('state') ||
        key.includes('code_verifier') ||
        key.includes('PKCE')
      );
      console.log('💾 [AuthService] localStorage AVANT initCodeFlow:', oauthKeysBefore);

      // Charger le discovery document d'abord (si pas déjà chargé)
      console.log('📡 [AuthService] Chargement du discovery document...');
      const discovery = await this.oauthService.loadDiscoveryDocument();
      console.log('✅ [AuthService] Discovery document chargé:', discovery);

      // Initier le flux de code avec PKCE
      console.log('🚀 [AuthService] Appel de initCodeFlow()...');

      // initCodeFlow() fait automatiquement:
      // 1. Génère un code_verifier et code_challenge (PKCE)
      // 2. Sauvegarde dans localStorage
      // 3. Redirige vers le serveur d'autorisation
      this.oauthService.initCodeFlow();

      // Cette ligne ne sera jamais exécutée car initCodeFlow redirige
      console.log('📍 [AuthService] Redirection vers le serveur d\'autorisation...');
    } finally {
      // Note: Ce finally ne sera jamais exécuté car initCodeFlow() redirige
      // mais on le garde pour la cohérence du code
      this.isLoggingIn = false;
    }
  }

  /**
   * Vérifie si l'utilisateur est authentifié
   */
  isAuthenticated(): boolean {
    const hasValidAccessToken = this.oauthService.hasValidAccessToken();
    const hasValidIdToken = this.oauthService.hasValidIdToken();
    return hasValidAccessToken && hasValidIdToken;
  }

  /**
   * Récupère l'access token
   */
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  /**
   * Récupère l'ID token
   */
  getIdToken(): string | null {
    return this.oauthService.getIdToken();
  }

  /**
   * Récupère les claims de l'utilisateur
   */
  getUserClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  /**
   * Déconnexion
   */
  logout(): void {
    // 1. Récupérer l'id_token pour le logout
    const idToken = this.oauthService.getIdToken();

    // 2. Construire l'URL de logout du serveur d'autorisation
    const logoutUrl = `${this.oauthService.issuer}/oauth2/logout`;
    const postLogoutRedirectUri = window.location.origin + '/login';

    // 3. Nettoyer localement
    this.oauthService.logOut();
    sessionStorage.clear();
    localStorage.clear();

    // 4. Rediriger vers le serveur d'autorisation pour logout complet
    window.location.href = `${logoutUrl}?` +
      `id_token_hint=${idToken}&` +
      `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
  }

  clearAllCookies() {
    const cookies = document.cookie.split(';');

    console.log('Cookies', cookies)
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      // Supprimer pour le domaine actuel
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

      // Supprimer pour tous les sous-domaines possibles
      const domain = window.location.hostname;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;

      // Supprimer pour le domaine parent
      const parts = domain.split('.');
      if (parts.length > 2) {
        const parentDomain = '.' + parts.slice(-2).join('.');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${parentDomain};`;
      }
    }
  }

  /**
   * Rafraîchit le token
   */
  async refreshToken(): Promise<void> {
    console.log('🔄 [AuthService] Rafraîchissement du token...');
    try {
      await this.oauthService.refreshToken();
      console.log('✅ [AuthService] Token rafraîchi avec succès');
    } catch (error) {
      console.error('❌ [AuthService] Erreur lors du rafraîchissement du token:', error);
      throw error;
    }
  }

  getIdentityClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  // Récupérer le thème depuis le token JWT
  getThemeFromToken(): string {
    const claims = this.getIdentityClaims();
    console.log('🎨 Récupération du thème depuis le token:', claims?.theme);
    return claims?.theme || 'light';
  }
}
